import { prepareCanvas } from "../lib/canvas";
import { mixHexColors } from "../lib/colors";
import { ease } from "../lib/ease";
const hoverDuration = 300;

function getTimelineValue(timelineValA, timeA, timeB, duration, direction = 1) {
  const deltaPerc = (timeB - timeA) / duration;
  return Math.min(1, Math.max(0, timelineValA + deltaPerc * direction));
}

function pointerWithDpi(pointer, dpi = 1) {
  return {
    x: pointer.x * dpi,
    y: pointer.y * dpi
  };
}

export async function circleProgressChart(canvas, {
  sets
} = {}, {
  onHover
} = {}) {
  let lastRenderHash = null;
  let hovered = null;
  const $sectionStates = new WeakMap();

  const getState = (section, then) => {
    if (!section) {
      return;
    }

    let state = $sectionStates.get(section);

    if (!state) {
      state = {
        active: true,
        timeline: {
          direction: 1,
          val: 1,
          duration: hoverDuration,
          active: false,
          timestamp: null
        }
      };
      $sectionStates.set(section, state);
    }

    then?.(state);
    return state;
  };

  function render(...args) {
    const hash = JSON.stringify(args);

    if (hash === lastRenderHash) {
      return;
    }

    let {
      pointer,
      sets,
      rect,
      dpi,
      userAction = false
    } = args[0];
    pointer = pointer ? pointerWithDpi(pointer, dpi) : null;
    sets = sets.filter(set => set.value > 0);
    canvas.width = rect.width * dpi;
    canvas.height = rect.width * dpi;

    if (!canvas.width || !canvas.height) {
      return;
    }

    const {
      width,
      height
    } = canvas;
    const {
      ctx
    } = prepareCanvas(canvas);
    let circleCount = 1;
    let lineWidth = 16 * dpi;
    let gap = 7 * dpi;

    let calcTotalRadius = () => lineWidth * sets.length + gap * (sets.length - 1);

    while (calcTotalRadius() * 2 + height * 0.3 > height) {
      lineWidth *= 0.9;
      gap *= 0.9;
    }

    const oldHovered = hovered;
    let newHovered = null;

    for (let set of sets) {
      let radius = height / 2 - lineWidth * circleCount - gap * (circleCount - 1);
      let centerX = width / 2;
      let centerY = height / 2;

      const isInteracting = () => {
        if (!pointer) {
          return false;
        }

        const dx = pointer.x - centerX;
        const dy = pointer.y - centerY;
        return Math.sqrt(dx * dx + dy * dy) >= radius - lineWidth / 2 - gap / 2 && Math.sqrt(dx * dx + dy * dy) <= radius + lineWidth / 2 + gap / 2; // && Math.atan2(dy, dx) >= startAngle && Math.atan2(dy, dx) <= endAngle;
      };

      if (isInteracting()) {
        newHovered = set;
      }

      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI, false);
      ctx.lineWidth = lineWidth;
      ctx.strokeStyle = "#141414";
      ctx.stroke();
      const state = getState(set); // rounded line cap

      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, -Math.PI / 2, -Math.PI / 2 + 2 * Math.PI * set.value / 100, false);
      ctx.lineWidth = lineWidth;
      ctx.strokeStyle = mixHexColors("#77777700", set.color, ease.easeInOutCubic(state.timeline.val));
      ctx.lineCap = "round";
      ctx.stroke();
      circleCount++;
    }

    if (!userAction) {
      return;
    }

    const now = Date.now();

    if (newHovered) {
      // canvas.style.cursor = "pointer";
      if (newHovered !== oldHovered) {
        ctrl.setActive(newHovered, true);
      }
    } else {
      // canvas.style.cursor = "default";
      if (oldHovered) {
        ctrl.blur();
      }
    }

    hovered = newHovered;
  }

  canvas.addEventListener("mousemove", e => {
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const dpi = window.devicePixelRatio || 1;
    render({
      pointer: {
        x,
        y
      },
      sets,
      rect,
      dpi,
      userAction: true
    });
  }, true);
  canvas.addEventListener("mouseleave", () => {
    render({
      sets,
      rect: canvas.getBoundingClientRect(),
      dpi: window.devicePixelRatio || 1,
      userAction: true
    });
  }, true);

  function animate() {
    const now = Date.now();
    let isAnimating = false;
    sets.forEach(set => {
      getState(set, state => {
        if (state.timeline.active) {
          state.timeline.val = getTimelineValue(state.timeline.val, state.timeline.timestamp, now, state.timeline.duration, state.timeline.direction);
          state.timeline.timestamp = now;

          if (state.timeline.direction === -1 && state.timeline.val === 0 || state.timeline.direction === 1 && state.timeline.val === 1) {
            state.timeline.active = false;
          } else {
            isAnimating = true;
          }
        }
      });
    });
    render({
      sets,
      rect: canvas.getBoundingClientRect(),
      dpi: window.devicePixelRatio || 1
    });

    if (isAnimating) {
      requestAnimationFrame(animate);
    }
  }

  const update = () => {
    render({
      sets,
      rect: canvas.getBoundingClientRect(),
      dpi: window.devicePixelRatio || 1
    });
  };

  let interval = setInterval(() => {
    if (!canvas.parentNode) {
      clearInterval(interval);
      return;
    }

    update();
  }, 1000);
  update();
  const ctrl = {
    canvas,
    update,
    destroy: () => {
      clearInterval(interval);
    },

    setActive(activeSet, direct = false, active = true) {
      sets.forEach(set => {
        getState(set, state => {
          if (activeSet === set) {
            state.active = true;
            state.timeline.direction = 1;
          } else {
            state.active = false;
            state.timeline.direction = -1;
          }

          state.timeline.active = true;
          state.timeline.timestamp = Date.now();
        });
      });
      onHover?.(activeSet, hovered, direct);
      hovered = activeSet;
      animate();
    },

    blur() {
      sets.forEach(set => {
        getState(set, state => {
          state.active = true;
          state.timeline.direction = 1;
          state.timeline.active = true;
          state.timeline.timestamp = Date.now();
        });
      });
      onHover?.(null, hovered);
      hovered = null;
      animate();
    }

  };
  return ctrl;
}